/**
 * Real material values for `hatchback.glb`.
 *
 * This file arrives with genuinely authored PBR: a clearcoat/specular/IOR
 * paint, alpha-blended glass, and six textures (tyre tread and normal map,
 * grille mesh, head/tail/indicator lenses). These specs are applied *onto*
 * the model's own materials rather than replacing them, so every texture
 * map survives — see `applyCarMaterials`. Only the values named here are
 * touched; anything not listed is left exactly as the model author set it.
 */
export const CAR_MATERIAL_LIBRARY = {
  // Body paint — vivid metallic Sebring-orange base coat + clear lacquer.
  carpaint: {
    color: "#e05a0e",
    metalness: 0.55,
    roughness: 0.28,
    clearcoat: 1,
    clearcoatRoughness: 0.06,
    envMapIntensity: 1.35,
    specularIntensity: 1,
    specularColor: "#ffffff",
  },

  // Windows and windshield (`CAM` is Turkish for glass). Reflection-led,
  // low transmission (no floor/room behind the car to transmit through),
  // a lifted blue-grey tint so it reads as glazing from every angle.
  CAM: {
    color: "#2e3d4e",
    transmission: 0.2,
    roughness: 0.15,
    thickness: 0.08,
    ior: 1.52,
    clearcoat: 1,
    clearcoatRoughness: 0.05,
    envMapIntensity: 2.2,
    transparent: false,
    opacity: 1,
  },

  // Headlight lens cover — clean, colourless, high-transmission lens.
  "ÖNFARCAM": {
    color: "#eef4f7",
    transmission: 0.85,
    roughness: 0.06,
    thickness: 0.12,
    ior: 1.5,
    clearcoat: 1,
    clearcoatRoughness: 0.03,
    envMapIntensity: 1.4,
    transparent: false,
    opacity: 1,
  },

  // Headlight internals — bright reflector so the lamp reads with depth.
  "önfarışıkları": {
    metalness: 0.5,
    roughness: 0.16,
    emissive: "#fff3d6",
    emissiveIntensity: 0.32,
    envMapIntensity: 1.5,
  },
  frontlight: {
    color: "#f2f2f0",
    metalness: 0.55,
    roughness: 0.15,
    envMapIntensity: 1.5,
  },

  // Tail lamps and indicators.
  rearlight: {
    metalness: 0.15,
    roughness: 0.18,
    emissive: "#8a0f16",
    emissiveIntensity: 0.32,
    envMapIntensity: 1.4,
  },
  sinyal: {
    metalness: 0.15,
    roughness: 0.18,
    emissive: "#7a3f08",
    emissiveIntensity: 0.22,
    envMapIntensity: 1.4,
  },

  // Chrome and bare metal trim.
  metal: { color: "#e9e9ee", metalness: 1, roughness: 0.12, envMapIntensity: 1.6 },
  // `ayna` — the door mirrors.
  ayna: { color: "#ececf1", metalness: 1, roughness: 0.06, envMapIntensity: 1.7 },

  // Wheels: dark machined rim, rubber keeps its tread/normal map.
  RimColor: { color: "#42444a", metalness: 0.95, roughness: 0.26, envMapIntensity: 1.3 },
  Tire: { color: "#141416", metalness: 0, roughness: 0.9, envMapIntensity: 0.5 },

  // Grille keeps its mesh texture, darkened to a satin plastic.
  grill: { color: "#17171a", metalness: 0.25, roughness: 0.62, envMapIntensity: 0.8 },

  // Interior — lifted from pure black just enough to catch light through
  // the glazing, kept dark since these also land on exterior trim.
  interior: { color: "#24262b", metalness: 0.05, roughness: 0.6, envMapIntensity: 1.2 },
  interior2: { color: "#1e2024", metalness: 0.05, roughness: 0.66, envMapIntensity: 1.2 },
};
